import { useContext, useEffect, useState } from "react"

import { useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  RecurringDepositoryTransaction,
  RecurringDepositoryTransactionCollection,
} from "wealthmode-frontend/backend"
import { formatRecurringAmount, formatRecurringDay } from "wealthmode-frontend/util"

import { DetailField, DetailLink, ResourceDetail } from "@/components"
import { BackendContext } from "@/context"

export default function RecurringDepositoryTransactionDetail() {
  const { client_id, account_id, recurring_depository_transaction_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
    recurring_depository_transaction_id: string
  }>()

  const backend = useContext(BackendContext)

  const [transaction, setTransaction] = useState<RecurringDepositoryTransaction>()

  useEffect(() => {
    ;(async () => {
      const clients = backend.collection("clients") as ClientCollection
      const client = clients.model(client_id)
      await client.fetch({ reload: true })

      const accounts = client.link("accounts")?.resolve() as AccountCollection
      const account = accounts.model(account_id)
      await account.fetch({ reload: true })

      const data = account.embed("data") as AccountData
      const transactions = data.link("recurring_depository_transactions")?.resolve() as
        RecurringDepositoryTransactionCollection | undefined
      if (!transactions) {
        return
      }

      const model = transactions.model(recurring_depository_transaction_id)
      await model.fetch({ reload: true })

      setTransaction(model)
    })().catch((err) => {
      console.error("Error: " + err)
      throw err
    })
  }, [backend, client_id, account_id, recurring_depository_transaction_id])

  const base = `/clients/${client_id}/accounts/${account_id}/recurring_depository_transactions/${recurring_depository_transaction_id}`

  const fields: DetailField[] = []
  const links: DetailLink[] = [{ href: `${base}/occurrences`, label: "Occurrences" }]

  if (transaction) {
    fields.push(
      { label: "Frequency", value: transaction.attributes.frequency },
      { label: "Interval", value: transaction.attributes.interval },
      { label: "Day", value: formatRecurringDay(transaction.attributes) },
      { label: "Start", value: transaction.attributes.start_date },
      { label: "End", value: transaction.attributes.end_date ?? "" },
      { label: "Confidence", value: transaction.attributes.confidence },
      { label: "Type", value: transaction.attributes.transaction_type },
      { label: "Amount Type", value: transaction.attributes.amount_type },
      { label: "Amount", value: formatRecurringAmount(transaction.attributes) },
    )
  }

  return (
    <ResourceDetail
      title="Recurring Depository Transaction"
      subtitle={recurring_depository_transaction_id}
      loading={!transaction}
      fields={fields}
      links={links}
    />
  )
}
